import type { Getter, Setter } from 'jotai';
import noop from 'lodash/noop';
import type { RangeSpanT } from '../../../../../common/types/types';
import { applyFrameRangeDeletion } from '../../../utils/relationships-utils';
import { relationshipStoreAtom } from '../../relationship-atoms';

/**
 * Trims or removes relationships whose frame ranges overlap a deleted annotation span.
 * Returns an undo callback. Returns `noop` when no relationships were affected.
 */
export function reviseRelationships(get: Getter, set: Setter, annotationId: string, deletedRange: RangeSpanT): () => void {
  const store = get(relationshipStoreAtom);
  const frameRange = {
    startFrameIndex: deletedRange.start,
    endFrameIndex: deletedRange.end
  };
  const affectedIds = Object.keys(store).filter(id => {
    const rel = store[id];
    return rel?.sourceAnnotationId === annotationId || rel?.targetAnnotationId === annotationId;
  });
  if (affectedIds.length === 0) {
    return noop;
  }
  const {
    previousSnapshot,
    updatedStore,
    createdIds
  } = applyFrameRangeDeletion(store, affectedIds, frameRange);
  if (Object.keys(previousSnapshot).length === 0) {
    return noop;
  }
  set(relationshipStoreAtom, updatedStore);
  return () => {
    set(relationshipStoreAtom, prev => {
      const next = {
        ...prev
      };
      for (const id of createdIds) delete next[id];
      return {
        ...next,
        ...previousSnapshot
      };
    });
  };
}